import {
  destroy,
  GetBoundingClientRectAdapter,
  getCurrentFocusKey,
  init,
  pause,
  resume,
  setKeyMap,
} from '@noriginmedia/norigin-spatial-navigation';

import { disposeEdgeCue, initEdgeCue } from './edgeCue';
import { ENTER_KEYS } from './keys';

export interface InitTvNavigationOptions {
  debug?: boolean;
  visualDebug?: boolean;
  /** Minimum ms between handled key-downs: 125 ≈ 8 moves/s (docs/04 §2). */
  throttle?: number;
  /**
   * Measure with `getBoundingClientRect` instead of `offsetLeft` chains. Required when
   * rows are translated with CSS transforms (virtualized rows) and in jsdom tests.
   */
  useGetBoundingClientRect?: boolean;
  /** Nudge the focused row/element when a press lands on an edge (UX-011). Default on. */
  edgeCue?: boolean;
}

let initialized = false;
let paused = false;

/**
 * Pausing norigin unbinds nothing, but it makes every arrow key a no-op — which looks exactly
 * like the focus loss of QA3 T1 and cannot be recovered from with the remote. So pausing goes
 * through here, the state is readable, and the focus watchdog treats "still paused" as a fault
 * it repairs (docs/04 §2.1). Never call norigin's `pause`/`resume` directly.
 */
export function pauseTvNavigation(): void {
  paused = true;
  pause();
}

export function resumeTvNavigation(): void {
  paused = false;
  resume();
}

export function isTvNavigationPaused(): boolean {
  return paused;
}

/** Initializes norigin once with the TV defaults. Safe to call repeatedly (HMR, tests). */
export function initTvNavigation({
  debug = false,
  visualDebug = false,
  throttle = 125,
  useGetBoundingClientRect = true,
  edgeCue = true,
}: InitTvNavigationOptions = {}): void {
  if (initialized) destroy();
  init({
    debug,
    visualDebug,
    throttle,
    throttleKeypresses: true,
    ...(useGetBoundingClientRect ? { layoutAdapter: GetBoundingClientRectAdapter } : {}),
    shouldFocusDOMNode: false,
    shouldUseNativeEvents: false,
    distanceCalculationMethod: 'center',
  });
  paused = false;
  setKeyMap({
    left: [37, 'ArrowLeft'],
    up: [38, 'ArrowUp'],
    right: [39, 'ArrowRight'],
    down: [40, 'ArrowDown'],
    enter: [...ENTER_KEYS],
  });
  // UX-011: no wrap at the ends, but never a dead press either.
  if (edgeCue) {
    initEdgeCue({
      throttleMs: throttle,
      isPaused: isTvNavigationPaused,
      getFocusKey: () => getCurrentFocusKey() ?? null,
    });
  } else {
    disposeEdgeCue();
  }
  initialized = true;
}

export function destroyTvNavigation(): void {
  if (!initialized) return;
  disposeEdgeCue();
  destroy();
  initialized = false;
}

/**
 * Read-only mode for hosts that render TV components **outside** a TV: the CMS home preview
 * (docs/06 §2.7) mounts `Row`/`Card`/`HeroCarousel`, whose focus hooks need the service to be
 * initialised (they measure their layout through it).
 *
 * QA 2026-09-15 UX-500: the previous version initialised norigin with
 * `shouldUseNativeEvents: false` and relied on `pause()` to keep the console usable. It does
 * not: norigin's adapter `preventDefault()`s and `stopPropagation()`s every **mapped** key on
 * `window` *before* the service consults its paused flag, so Enter and the four arrows died on
 * every route of the console — the login form could not be submitted with Enter and the caret
 * could not be moved inside any text field.
 *
 * So preview mode now:
 *  - uses `shouldUseNativeEvents: true`, so norigin never cancels a key press, ever;
 *  - is **scoped to the preview container**: a capture-phase guard on `window` resumes the
 *    service only while the key press comes from inside the stage and pauses it again for
 *    everything else, so a key pressed in a form can never reach spatial navigation;
 *  - is **mounted-scoped**: `initPreviewNavigation` returns a disposer the host calls on
 *    unmount, so nothing at all is bound while the preview is off screen.
 *
 * Callers must also import the preview lazily: pulling it into the main bundle is what put a
 * `window` key listener on all fifteen CMS routes in the first place.
 */
export interface PreviewNavigationOptions {
  /** The preview stage. Only key presses originating inside it may reach the service. */
  container: HTMLElement;
}

export function initPreviewNavigation({ container }: PreviewNavigationOptions): () => void {
  if (initialized) destroy();
  init({
    throttle: 0,
    throttleKeypresses: false,
    layoutAdapter: GetBoundingClientRectAdapter,
    shouldFocusDOMNode: false,
    // Never `preventDefault()`/`stopPropagation()` a key press in a console full of inputs.
    shouldUseNativeEvents: true,
  });
  initialized = true;
  pauseTvNavigation();

  const scope = (event: KeyboardEvent) => {
    const target = event.target;
    const inside = target instanceof Node && container.contains(target);
    if (inside === paused) {
      if (inside) resumeTvNavigation();
      else pauseTvNavigation();
    }
  };
  window.addEventListener('keydown', scope, true);
  window.addEventListener('keyup', scope, true);

  return () => {
    window.removeEventListener('keydown', scope, true);
    window.removeEventListener('keyup', scope, true);
    destroyTvNavigation();
  };
}
